import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { normalizeKeyword } from "@/lib/keywordGroups";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface RankEntry {
  id: string;
  keyword: string;
  rank: number | null;
  url: string | null;
  checked_at: string;
  source: string | null;
}

interface GroupRow {
  id: string;
  canonical_keyword: string;
  normalized_key: string;
  total_impressions: number;
  best_position: number | null;
  status: string;
  variants: string[] | any;
}

type Row =
  | {
      kind: "ranked";
      id: string;
      keyword: string;
      rank: number;
      url: string | null;
      checked_at: string;
      previousRank: number | null;
      variants: string[];
      totalImpressions: number;
    }
  | {
      kind: "outside";
      id: string;
      keyword: string;
      checked_at: string;
      variants: string[];
    }
  | {
      kind: "pending";
      id: string;
      keyword: string;
      variants: string[];
    };

const RankingsTab = ({ clientIds }: { clientIds: string[] }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (clientIds.length === 0) {
      setLoading(false);
      return;
    }

    const fetchAll = async () => {
      const [{ data: rankData }, { data: groupRows }] = await Promise.all([
        supabase
          .from("rank_tracking")
          .select("*")
          .in("client_id", clientIds)
          .order("checked_at", { ascending: false }),
        supabase
          .from("keyword_groups")
          .select("id, canonical_keyword, normalized_key, total_impressions, best_position, status, variants")
          .in("client_id", clientIds),
      ]);

      const groups: GroupRow[] = ((groupRows || []) as any[]).map((g) => ({
        ...g,
        variants: Array.isArray(g.variants) ? g.variants : [],
      }));

      // Build latest + previous rank by NORMALIZED key, only counting GSC-confirmed entries.
      // serper-only entries (source=serper AND no impressions in any variant) are dropped.
      const totalImpByKey = new Map<string, number>();
      groups.forEach((g) => totalImpByKey.set(g.normalized_key, g.total_impressions || 0));

      const latest = new Map<string, RankEntry>();
      const previous = new Map<string, number | null>();
      for (const entry of (rankData || []) as RankEntry[]) {
        const key = normalizeKeyword(String(entry.keyword || ""));
        if (!key) continue;
        const src = (entry.source || "").toLowerCase();
        const totalImp = totalImpByKey.get(key) ?? 0;
        const isGscConfirmed = src === "gsc" || totalImp > 0;
        if (!isGscConfirmed) continue;
        if (!latest.has(key)) latest.set(key, entry);
        else if (!previous.has(key)) previous.set(key, entry.rank);
      }

      const ranked: Row[] = [];
      const outside: Row[] = [];
      const pending: Row[] = [];

      // Track most recent rank_tracking entry per key (regardless of GSC confirmation),
      // so we can distinguish "checked but outside top 100" from "never checked".
      const latestAny = new Map<string, RankEntry>();
      for (const entry of (rankData || []) as RankEntry[]) {
        const key = normalizeKeyword(String(entry.keyword || ""));
        if (!key) continue;
        if (!latestAny.has(key)) latestAny.set(key, entry);
      }

      for (const g of groups) {
        const key = g.normalized_key;
        const variants = (g.variants as string[]) || [];
        const entry = latest.get(key);
        const anyEntry = latestAny.get(key);
        if (entry && entry.rank != null) {
          ranked.push({
            kind: "ranked",
            id: g.id,
            keyword: g.canonical_keyword,
            rank: entry.rank,
            url: entry.url,
            checked_at: entry.checked_at,
            previousRank: previous.get(key) ?? null,
            variants,
            totalImpressions: g.total_impressions || 0,
          });
        } else if (g.best_position != null) {
          // GSC has a position but no rank_tracking entry yet — show GSC position directly.
          ranked.push({
            kind: "ranked",
            id: g.id,
            keyword: g.canonical_keyword,
            rank: Math.round(Number(g.best_position)),
            url: null,
            checked_at: new Date().toISOString(),
            previousRank: null,
            variants,
            totalImpressions: g.total_impressions || 0,
          });
        } else if (anyEntry && anyEntry.source && anyEntry.rank == null) {
          // Checked recently but outside top 100 — honest signal, not "pending".
          outside.push({
            kind: "outside",
            id: g.id,
            keyword: g.canonical_keyword,
            checked_at: anyEntry.checked_at,
            variants,
          });
        } else {
          pending.push({ kind: "pending", id: g.id, keyword: g.canonical_keyword, variants });
        }
      }
      ranked.sort((a, b) => (a as any).rank - (b as any).rank);

      setRows([...ranked, ...outside, ...pending]);
      setLoading(false);
    };

    fetchAll();
  }, [clientIds]);

  const getRankColor = (rank: number) => {
    if (rank <= 10) return "text-primary";
    if (rank <= 30) return "text-amber-500 dark:text-amber-400";
    return "text-destructive";
  };
  const getRankBg = (rank: number) => {
    if (rank <= 10) return "bg-primary/10";
    if (rank <= 30) return "bg-amber-500/10";
    return "bg-destructive/10";
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-14 animate-pulse rounded-lg border border-border bg-muted" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-center shadow-[0_8px_28px_hsl(var(--foreground)/0.08)]">
        <p className="text-sm font-mono text-muted-foreground">No keywords tracked yet.</p>
        <p className="mt-2 text-xs font-mono text-muted-foreground">Keywords will appear after the first website scan.</p>
      </div>
    );
  }

  const VariantsBadge = ({ variants }: { variants: string[] }) => {
    if (!variants || variants.length <= 1) return null;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="ml-1 cursor-help rounded-[5px] border border-border bg-muted/60 px-1.5 py-0.5 text-[9px] font-mono font-bold text-muted-foreground"
            aria-label={`${variants.length} variants`}
          >
            {variants.length} variants
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px]">
          <div className="text-[10.5px] font-mono">
            <div className="mb-1 font-bold text-muted-foreground">Combined variants:</div>
            <ul className="space-y-0.5">
              {variants.map((v) => (
                <li key={v}>• {v}</li>
              ))}
            </ul>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-2">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          <div className="col-span-5">Keyword</div>
          <div className="col-span-3 text-center">Rank</div>
          <div className="col-span-2 text-center">Trend</div>
          <div className="col-span-2 text-right">Checked</div>
        </div>

        {rows.map((row, i) => {
          if (row.kind === "pending") {
            return (
              <motion.div
                key={`p-${row.id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="grid grid-cols-12 items-center gap-2 rounded-lg border border-dashed border-border bg-muted/30 px-3 py-3"
              >
                <div className="col-span-5 min-w-0">
                  <p className="truncate text-xs font-mono text-foreground">
                    {row.keyword}
                    <VariantsBadge variants={row.variants} />
                  </p>
                </div>
                <div className="col-span-7">
                  <div className="flex items-center justify-end gap-2 sm:justify-center">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
                    <span className="text-[11px] font-mono text-muted-foreground">
                      Collecting Google data — ready within 24 hours
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          }
          if (row.kind === "outside") {
            return (
              <motion.div
                key={`o-${row.id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="grid grid-cols-12 items-center gap-2 rounded-lg border border-border bg-card/60 px-3 py-3"
              >
                <div className="col-span-5 min-w-0">
                  <p className="truncate text-xs font-mono text-foreground">
                    {row.keyword}
                    <VariantsBadge variants={row.variants} />
                  </p>
                </div>
                <div className="col-span-3 text-center">
                  <span className="whitespace-nowrap rounded bg-muted px-2 py-0.5 text-[11px] font-mono font-bold text-muted-foreground">
                    Outside top 100
                  </span>
                </div>
                <div className="col-span-2 text-center text-sm text-muted-foreground/60">·</div>
                <div className="col-span-2 text-right">
                  <span className="text-[10px] font-mono text-muted-foreground">{timeAgo(row.checked_at)}</span>
                </div>
              </motion.div>
            );
          }

          const hasEnoughSignal = (row.totalImpressions ?? 0) >= 5;
          const trend =
            !hasEnoughSignal || row.previousRank == null
              ? null
              : row.rank < row.previousRank
              ? "up"
              : row.rank > row.previousRank
              ? "down"
              : "same";

          return (
            <motion.div
              key={row.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.02 }}
              className="grid grid-cols-12 items-center gap-2 rounded-lg border border-border bg-card px-3 py-3 shadow-[0_8px_28px_hsl(var(--foreground)/0.05)]"
            >
              <div className="col-span-5 min-w-0">
                <p className="truncate text-xs font-mono text-foreground">
                  {row.keyword}
                  <VariantsBadge variants={row.variants} />
                </p>
                {row.url && (
                  <p className="mt-0.5 truncate text-[9px] font-mono text-muted-foreground">{row.url}</p>
                )}
              </div>
              <div className="col-span-3 text-center">
                <span
                  className={`whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-mono font-bold ${getRankBg(
                    row.rank,
                  )} ${getRankColor(row.rank)}`}
                >
                  {`Position ${row.rank}`}
                </span>
              </div>
              <div className="col-span-2 text-center text-sm">
                {trend === "up" && <span className="text-primary">▲</span>}
                {trend === "down" && <span className="text-destructive">▼</span>}
                {trend === "same" && <span className="text-muted-foreground">—</span>}
                {trend === null && <span className="text-muted-foreground/60">·</span>}
              </div>
              <div className="col-span-2 text-right">
                <span className="text-[10px] font-mono text-muted-foreground">{timeAgo(row.checked_at)}</span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </TooltipProvider>
  );
};

export default RankingsTab;
